import * as Color from 'color';
import * as React from 'react';

import {useCSS} from '@pinecast/styles';
import {defineMessages, useIntl} from '@pinecast/i18n';
import {MOBILE} from './mobile';
import {useTheme} from './ThemeProvider';

const messages = defineMessages({
  label: {
    id: 'pl-spa.VolumeControl.label',
    description: 'Label for the player seek bar',
    defaultMessage: 'Volume',
  },
  valueText: {
    id: 'pl-spa.VolumeControl.valueText',
    description:
      'Value text for the player seek bar. {percent} is the percent of 100 that the volume is set to',
    defaultMessage: '{percent} percent',
  },
});

type Props = {
  audio: HTMLAudioElement;
};

const VolumeControl = ({audio}: Props) => {
  const css = useCSS();
  const intl = useIntl();

  const theme = useTheme();
  const color = Color(theme.primaryColor).isDark() ? '#ddd' : '#444';

  const volumeControl = React.useRef<HTMLDivElement | null>(null);

  const volumeRef = React.useRef(audio.volume);
  const [volume, setVolume] = React.useState(audio.volume);

  const dragging = React.useRef<boolean>(false);

  React.useEffect(() => {
    volumeRef.current = audio.volume;
    setVolume(volumeRef.current || 0);
    const onVolumeChange = () => {
      setVolume(audio.volume);
      volumeRef.current = audio.volume;
    };
    audio.addEventListener('volumechange', onVolumeChange);

    let rect: DOMRect | null = null;

    function setFromX(x: number) {
      if (!dragging.current || !rect) {
        return;
      }
      const percent = Math.max(Math.min((x - rect.left) / rect.width, 1), 0);
      setVolume((audio.volume = percent));
    }

    volumeControl.current!.addEventListener('mousedown', (e: MouseEvent) => {
      e.stopPropagation();
      dragging.current = true;
      rect = volumeControl.current!.getClientRects()[0];
      setFromX(e.clientX);
    });
    const endDragging = (e: MouseEvent) => {
      if (!dragging.current) {
        return;
      }
      setFromX(e.clientX);
      dragging.current = false;
    };
    const mouseMove = (e: MouseEvent) => {
      setFromX(e.clientX);
    };
    document.body.addEventListener('mouseup', endDragging);
    document.body.addEventListener('mouseleave', endDragging);
    document.body.addEventListener('mousemove', mouseMove);

    return () => {
      audio.removeEventListener('volumechange', onVolumeChange);

      document.body.removeEventListener('mouseup', endDragging);
      document.body.removeEventListener('mouseleave', endDragging);
      document.body.removeEventListener('mousemove', mouseMove);

      dragging.current = false;
    };
  }, [audio]);

  const keyUp = React.useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.keyCode === 37) {
        // left
        if (audio.src) {
          setVolume(Math.max(0, (audio.volume -= 0.1)));
        }
      } else if (e.keyCode === 39) {
        // right
        if (audio.src) {
          setVolume(Math.min(1, (audio.volume += 0.1)));
        }
      }
    },
    [audio],
  );

  return (
    <div
      className={css({
        contain: 'size layout',
        display: 'flex',
        alignItems: 'center',
      })}
    >
      <div
        onKeyUp={keyUp}
        className={css({
          flex: '1 1',
          padding: '8px 12px',
          [MOBILE]: {padding: '6px 8px'},
        })}
        ref={volumeControl}
      >
        <div
          className={css({
            borderRadius: '4px',
            background: 'rgba(127, 127, 127, 0.2)',
            height: '8px',
          })}
        >
          <div
            className={css({
              backgroundColor: '#fff',
              border: '1px solid rgba(0, 0, 0, 0.3)',
              borderRadius: '12px',
              boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1), 0 0 0 0 transparent',
              transform: 'translateX(-6px)',
              left: `${volume * 100}%`,
              top: '-4px',
              position: 'relative',
              transition: `box-shadow 0.2s`,
              height: '16px',
              width: '16px',
              ':focus': {
                outline: 'none',
                boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1), 0 0 0 2px #06f',
              },

              [MOBILE]: {
                height: '12px',
                width: '12px',
                top: '-2px',
                transform: 'translateX(-4px)',
              },
            })}
            tabIndex={0}
            role="slider"
            title={intl.formatMessage(messages.label)}
            aria-label={intl.formatMessage(messages.label)}
            aria-orientation="horizontal"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={volume * 100}
            aria-valuetext={intl.formatMessage(messages.valueText, {
              percent: Math.round(volume * 100),
            })}
          ></div>
        </div>
      </div>
      <svg
        viewBox="0 0 32 32"
        height={12}
        width={12}
        xmlns="http://www.w3.org/2000/svg"
        style={{marginLeft: '4px'}}
      >
        <path
          fill={color}
          d="M13.73 3.05l-7.9 7.9a.33.33 0 01-.24.1H2.67A2.67 2.67 0 000 13.72v4.55a2.67 2.67 0 002.67 2.67h2.92a.33.33 0 01.24.1l7.9 7.9A1.33 1.33 0 0016 28V4a1.33 1.33 0 00-2.27-.95z"
        />
        {volume > 0.3 && (
          <path
            fill={color}
            d="M20.19 10a1.33 1.33 0 10-1.89 1.88 5.84 5.84 0 010 8.24A1.33 1.33 0 1020.19 22a8.5 8.5 0 000-12z"
          />
        )}
        {volume > 0.6 && (
          <path
            fill={color}
            d="M23.45 6.62a1.33 1.33 0 00-1.88 1.89 10.6 10.6 0 010 14.98 1.33 1.33 0 001.88 1.89 13.26 13.26 0 000-18.76z"
          />
        )}
        {volume > 0.9 && (
          <path
            fill={color}
            d="M26.72 3.25a1.33 1.33 0 00-1.88 1.88 15.39 15.39 0 010 21.73 1.33 1.33 0 101.88 1.89 18.06 18.06 0 000-25.5z"
          />
        )}
      </svg>
    </div>
  );
};

export default VolumeControl;
